import type { CSSProperties, JSX } from 'react';
import { hexWithAlpha, type Palette } from '../../../_theme/palette.js';
import type { SurveyTheme } from '../types.js';

interface BrandingMarkProps {
  palette: Palette;
  theme: SurveyTheme | undefined;
  hideBranding?: boolean;
  // Top spacing in px from the preceding content. Defaults to 32 so the mark
  // sits with breathing room below the last question; surfaces with their own
  // tight spacing (welcome/thankyou) can pass a smaller value.
  topGap?: number;
}

// "Powered by ChirpBack" mark rendered INSIDE the surface content, just above
// the nav footer. Sitting inside the card (rather than as a separate band
// below the wizard footer) reads as part of the survey design instead of a
// floating credit. Mirrors the main app's hosted-page placement.
export function BrandingMark({ palette, theme, hideBranding, topGap = 32 }: BrandingMarkProps): JSX.Element | null {
  if (hideBranding) return null;
  const showPoweredBy = theme?.branding?.showFooter !== false;
  if (!showPoweredBy) return null;
  const customText = theme?.branding?.footerText?.trim();

  return (
    <div style={wrapperStyle(topGap)}>
      {customText
        ? <span style={{ color: palette.textMuted, fontSize: 11, letterSpacing: '0.01em' }}>{customText}</span>
        : <PoweredByPill palette={palette} />}
    </div>
  );
}

function PoweredByPill({ palette }: { palette: Palette }): JSX.Element {
  return (
    <span style={pillStyle(palette)}>
      <span aria-hidden style={pillDotStyle(palette)} />
      <span style={{ opacity: 0.85 }}>Powered by</span>
      <span style={pillBrandStyle(palette)}>ChirpBack</span>
    </span>
  );
}

function wrapperStyle(topGap: number): CSSProperties {
  return {
    display: 'flex',
    justifyContent: 'center',
    marginTop: topGap,
  };
}

function pillStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    border: `1px solid ${hexWithAlpha(palette.border, 0.65)}`,
    backgroundColor: hexWithAlpha(palette.textMuted, 0.04),
    color: palette.textMuted,
    fontSize: 11,
    letterSpacing: '0.01em',
    lineHeight: 1,
  };
}

function pillDotStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-block',
    height: 5,
    width: 5,
    borderRadius: 999,
    backgroundColor: palette.primary,
    boxShadow: `0 0 0 2px ${hexWithAlpha(palette.primary, 0.18)}`,
  };
}

function pillBrandStyle(palette: Palette): CSSProperties {
  return {
    fontWeight: 600,
    color: palette.text,
    letterSpacing: '-0.01em',
  };
}
